import cn from 'classnames';
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import {
  ONBOARDING_POPOVER_GAP as POPOVER_GAP,
  ONBOARDING_POPOVER_WIDTH as POPOVER_WIDTH,
  ONBOARDING_VIEWPORT_MARGIN as VIEWPORT_MARGIN,
  patientAgentMessages,
} from '../config';
import { OnboardingFlow, OnboardingFlowStep, safeQuerySelector } from '../lib';

import styles from './Onboarding.module.scss';

type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
};

const readRect = (element: Element): Rect => {
  const box = element.getBoundingClientRect();
  return {
    top: box.top,
    left: box.left,
    width: box.width,
    height: box.height,
    bottom: box.bottom,
    right: box.right,
  };
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max));

type PopoverPos = { top: number; left: number };

const placePopover = (
  target: Rect,
  placement: OnboardingFlowStep['placement'],
  popoverHeight: number,
): PopoverPos => {
  const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  const maxTop = window.innerHeight - popoverHeight - VIEWPORT_MARGIN;
  const clampLeft = (left: number): number =>
    clamp(left, VIEWPORT_MARGIN, maxLeft);
  const clampTop = (top: number): number => clamp(top, VIEWPORT_MARGIN, maxTop);
  if (placement === 'left' || placement === 'right') {
    const left =
      placement === 'left'
        ? target.left - POPOVER_WIDTH - POPOVER_GAP
        : target.right + POPOVER_GAP;
    return {
      top: clampTop(target.top + target.height / 2 - popoverHeight / 2),
      left: clampLeft(left),
    };
  }
  const below = target.bottom + POPOVER_GAP;
  const above = target.top - POPOVER_GAP - popoverHeight;
  const belowFits =
    below + popoverHeight <= window.innerHeight - VIEWPORT_MARGIN;
  const aboveFits = above >= VIEWPORT_MARGIN;
  if (placement === 'top') {
    return {
      top: clampTop(aboveFits ? above : below),
      left: clampLeft(target.left),
    };
  }
  return {
    top: clampTop(belowFits ? below : above),
    left: clampLeft(target.left),
  };
};

type TourStep = { step: OnboardingFlowStep; index: number };

/**
 * Fullscreen portal overlay that walks the user through the flow steps:
 * each step scrolls its target into view, dims the rest of the page around
 * it, and anchors a small popover with the explanation.
 */
export const OnboardingTour: FC<{
  flow: OnboardingFlow;
  onClose: () => void;
}> = ({ flow, onClose }) => {
  const { formatMessage, formatNumber } = useIntl();
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverHeight, setPopoverHeight] = useState(160);
  const [target, setTarget] = useState<Rect | null>(null);
  const [current, setCurrent] = useState<TourStep | null>(null);
  // Keep onClose in a ref: the parent passes an unstable inline callback, so
  // effects keyed on its identity would re-run on every parent render and
  // reset the tour to its first step mid-walkthrough.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  // In the Fullscreen API only the fullscreen element (and its subtree)
  // renders, so body-level portals disappear. Host the tour inside the
  // fullscreen element while one is active.
  const [portalHost, setPortalHost] = useState<HTMLElement>(
    () => document.body,
  );
  const stepIndex = current?.index ?? -1;

  useEffect(() => {
    const onFullscreenChange = () =>
      setPortalHost(
        (document.fullscreenElement as HTMLElement | null) ?? document.body,
      );
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Match selectors after mount, not during render: the page behind the
  // portal may still be committing when the tour mounts, and render-phase
  // DOM reads are impure.
  const [matchedIndexes, setMatchedIndexes] = useState<number[]>([]);
  // True once the match pass has run at least once for the current flow.
  // The auto-start effect must not close on the empty pre-match state: on
  // mount both effects fire in the same commit and matchedIndexes is still
  // [], so an ungated close would kill a freshly opened tour instantly.
  const [matchSettled, setMatchSettled] = useState(false);
  useEffect(() => {
    const indexes: number[] = [];
    flow.steps.forEach((step, index) => {
      if (safeQuerySelector(step.selector)) {
        indexes.push(index);
      }
    });
    setMatchedIndexes(indexes);
    setMatchSettled(true);
  }, [flow]);

  const show = useCallback(
    (index: number) => {
      const step = flow.steps[index];
      const element = step ? safeQuerySelector(step.selector) : null;
      if (!step || !element) {
        onCloseRef.current();
        return;
      }
      element.scrollIntoView({ block: 'center', inline: 'nearest' });
      setCurrent({ step, index });
      // Measure after the scroll settles; browsers apply scrollIntoView
      // synchronously for 'auto' behavior in most cases, and the rect is
      // refreshed again on scroll anyway.
      setTarget(readRect(element));
    },
    [flow],
  );

  useEffect(() => {
    if (!matchSettled) {
      return;
    }
    if (matchedIndexes.length === 0) {
      onCloseRef.current();
      return;
    }
    show(matchedIndexes[0]);
  }, [matchedIndexes, matchSettled, show]);

  // Escape closes the tour; Tab is trapped inside the popover so keyboard
  // focus cannot wander into the dimmed background; scroll and resize
  // reposition the overlay.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const popover = popoverRef.current;
      if (!popover) {
        return;
      }
      const focusable = Array.from(
        popover.querySelectorAll<HTMLElement>('button:not([disabled])'),
      );
      if (focusable.length === 0) {
        return;
      }
      const active = document.activeElement;
      const inside = popover.contains(active);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        (event.shiftKey && (!inside || active === first)) ||
        (!inside && !event.shiftKey)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    let frame = 0;
    const refresh = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!current) {
          return;
        }
        const element = safeQuerySelector(current.step.selector);
        if (!element) {
          onCloseRef.current();
          return;
        }
        setTarget(readRect(element));
      });
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', refresh, true);
    window.addEventListener('resize', refresh);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', refresh, true);
      window.removeEventListener('resize', refresh);
    };
  }, [current]);

  useLayoutEffect(() => {
    if (popoverRef.current) {
      setPopoverHeight(popoverRef.current.offsetHeight);
    }
  }, [current]);

  const advance = (direction: 1 | -1) => {
    const candidates =
      direction === 1
        ? matchedIndexes.filter((i) => i > stepIndex)
        : matchedIndexes.filter((i) => i < stepIndex);
    const next =
      direction === 1 ? candidates[0] : candidates[candidates.length - 1];
    if (next === undefined) {
      onCloseRef.current();
      return;
    }
    show(next);
  };

  // The dialog in a portal must take focus (screen readers announce it, and
  // keyboard users land inside it) and hand it back on unmount. These hooks
  // sit above every early return: hook order must not depend on whether a
  // step is currently shown.
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    return () => previousFocusRef.current?.focus();
  }, []);
  useEffect(() => {
    if (current) {
      popoverRef.current?.focus();
    }
  }, [current?.index]);

  if (!current || !target) {
    return null;
  }
  const popover = placePopover(target, current.step.placement, popoverHeight);
  const position = matchedIndexes.indexOf(current.index) + 1;
  const isFirst = position === 1;
  const isLast = position === matchedIndexes.length;

  const dialogLabel = current.step.title || flow.title || undefined;

  return createPortal(
    <div className={styles.tour} role="dialog" aria-label={dialogLabel}>
      <div
        className={styles.tourHighlight}
        style={{
          top: target.top - 4,
          left: target.left - 4,
          width: target.width + 8,
          height: target.height + 8,
        }}
      />
      <div
        ref={popoverRef}
        tabIndex={-1}
        className={styles.tourPopover}
        style={{ top: popover.top, left: popover.left }}
      >
        {current.step.title || flow.title ? (
          <strong className={styles.tourStepTitle}>
            {current.step.title || flow.title}
          </strong>
        ) : null}
        <p className={cn('p2', styles.tourStepText)}>{current.step.text}</p>
        <div className={styles.tourControls}>
          <span className={cn('p3', styles.tourCounter)}>
            {formatNumber(position)} / {formatNumber(matchedIndexes.length)}
          </span>
          <div className={styles.tourButtons}>
            {!isFirst ? (
              <button
                type="button"
                className={styles.tourButton}
                onClick={() => advance(-1)}
              >
                {formatMessage(patientAgentMessages.onboardingBack)}
              </button>
            ) : null}
            <button
              type="button"
              className={cn(styles.tourButton, styles.tourButtonPrimary)}
              onClick={() => advance(1)}
            >
              {isLast
                ? formatMessage(patientAgentMessages.onboardingFinish)
                : formatMessage(patientAgentMessages.onboardingNext)}
            </button>
          </div>
        </div>
      </div>
    </div>,
    portalHost,
  );
};
